import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { isUnspecifiedDisease } from "@domain/medical-condition/medical-condition";
import { useFormatMedicalCondition } from "@domain/medical-condition/use-medical-condition";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";
import { PatientStatus, RegisteredPatient } from "@domain/patient/patient";
import {
	AnalyticsProgramClickType,
	AnalyticsProgramLocation,
	sendAnalyticsEventViewPatientDetail,
} from "@domain/program/program-analytics";
import {
	isGlobalProgramStatusExtendable,
	sortGlobalProgramStatus,
} from "@domain/program/program-status/program-status-utils";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { ConditionalSkeleton, ConditionalSkeletonProps } from "@ui/components/common/conditional-skeleton";
import { ProgramStatusComponent } from "@ui/components/program/program-status-component";
import { Tooltip } from "@ui/components/common/tooltip";
import { ResponsiveDeviceType, useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { PatientActionButton } from "@ui/pages/home/components/patient-action-button";
import { RegisteredPatientListingRowMobile } from "@ui/pages/home/components/registered-patient-listing-row-mobile";
import { usePatientListingItem } from "@ui/pages/home/hooks/use-patient-listing-item";
import {
	EmptyPatientMessage,
	EmptyPatientMessageVariant,
} from "@ui/pages/patient-details/components/empty-patient-message";
import { useProgramActions } from "@ui/pages/program-builder/hooks/use-program-actions";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import { Column } from "primereact/column";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import React, { useCallback, useMemo } from "react";

export type ExtendedRegisteredPatient = Omit<RegisteredPatient, "disease"> & {
	name: string | undefined;
	disease: string | undefined;
};

// cell components moved outside the main component to avoid inline definitions
const NameDiseaseCell = (patient?: ExtendedRegisteredPatient) => {
	if (!patient) {
		return null;
	}

	return (
		<PatientNameContainer>
			<PatientName className="p-size-s-bold">{patient.name}</PatientName>
			<Disease className="p-size-xs-semi-bold">{patient.disease}</Disease>
		</PatientNameContainer>
	);
};

const ProgramStatusCell = (patient?: RegisteredPatient) => {
	if (!patient) {
		return null;
	}

	return <ProgramStatusComponent programStatus={patient.globalProgramStatus} />;
};

const ComplianceCell: React.FC<{ patient: RegisteredPatient | undefined; isLoadingCompliances: boolean }> = ({
	patient,
	isLoadingCompliances,
}) => {
	const { format, formatPercent } = useI18n();
	const emptyWording = format("global.empty");
	const complianceSkeletonProps = useMemo(
		() => skeletonPropsFromLoadingState(isLoadingCompliances),
		[isLoadingCompliances],
	);
	return (
		<ConditionalSkeleton {...complianceSkeletonProps}>
			{!patient || isNil(patient.compliance) ? (
				emptyWording
			) : (
				<span className="p-size-s-bold">{formatPercent(patient.compliance)}</span>
			)}
		</ConditionalSkeleton>
	);
};

const LastSessionCell = ({
	patient,
	emptyWording,
	getFormattedLastSessionDate,
}: {
	patient?: RegisteredPatient;
	emptyWording: string;
	getFormattedLastSessionDate: (date: NonNullable<RegisteredPatient["lastCompletedSessionDate"]>) => string;
}) => {
	if (!patient) {
		return emptyWording;
	}

	return patient.lastCompletedSessionDate
		? getFormattedLastSessionDate(patient.lastCompletedSessionDate)
		: emptyWording;
};

const ExtendProgramCell = ({
	patient,
	label,
	extendProgram,
}: {
	patient?: RegisteredPatient;
	label: string;
	extendProgram: ReturnType<typeof useProgramActions>["extendProgram"];
}) => {
	if (!patient) {
		return null;
	}

	const { globalProgramStatus } = patient;
	if (!isGlobalProgramStatusExtendable(globalProgramStatus)) return null;
	return (
		<Button
			label={label}
			onClick={() =>
				extendProgram(
					AnalyticsProgramLocation.HOME,
					patient.id,
					patient.analyticsId,
					globalProgramStatus.programId,
					globalProgramStatus.type,
				)
			}
			uiStyle="secondary"
		/>
	);
};

const PatientActionCell = (data?: RegisteredPatient) => {
	if (!data) {
		return null;
	}

	return <PatientActionButton patient={{ ...data, status: PatientStatus.Registered }} />;
};

interface RegisteredPatientProps {
	registeredPatients: RegisteredPatient[];
	isLoadingCompliances: boolean;
}

const skeletonPropsFromLoadingState = (loadingState: boolean): ConditionalSkeletonProps => ({
	active: loadingState,
	loading: loadingState,
});

export const RegisteredPatientListing: React.FC<RegisteredPatientProps> = ({
	registeredPatients,
	isLoadingCompliances,
}) => {
	const { format } = useI18n();
	const { getFormattedLastSessionDate } = usePatientListingItem();
	const { formatDisease } = useFormatMedicalCondition();
	const { formatPatientName } = useFormatPatients();
	const { extendProgram } = useProgramActions();
	const navigate = useNavigate();

	const columnProps = { sortable: true };
	const registeredPatientsRows = registeredPatients.map(
		(patient): ExtendedRegisteredPatient => ({
			...patient,
			name: formatPatientName(patient),
			disease: isUnspecifiedDisease(patient.disease) ? undefined : formatDisease(patient.disease),
		}),
	);
	const { responsiveDeviceType } = useDimensions();

	const complianceSkeletonProps = useMemo(
		() => skeletonPropsFromLoadingState(isLoadingCompliances),
		[isLoadingCompliances],
	);

	const complianceBody = useCallback(
		(data: RegisteredPatient) => <ComplianceCell patient={data} isLoadingCompliances={isLoadingCompliances} />,
		[isLoadingCompliances],
	);

	const emptyWording = format("global.empty");
	const extendProgramLabel = format("home.registeredPatientsListing.extendColumn.button");

	const lastSessionBody = useCallback(
		(data: RegisteredPatient) => (
			<LastSessionCell
				patient={data}
				emptyWording={emptyWording}
				getFormattedLastSessionDate={getFormattedLastSessionDate}
			/>
		),
		[emptyWording, getFormattedLastSessionDate],
	);

	const extendProgramBody = useCallback(
		(data: RegisteredPatient) => (
			<ExtendProgramCell patient={data} label={extendProgramLabel} extendProgram={extendProgram} />
		),
		[extendProgram, extendProgramLabel],
	);

	if (responsiveDeviceType !== ResponsiveDeviceType.Desktop) {
		return registeredPatientsRows.length === 0 ? (
			<EmptyPatientMessage variant={EmptyPatientMessageVariant.NoRegisteredPatients} />
		) : (
			registeredPatientsRows.map(patient => (
				<RegisteredPatientListingRowMobile
					key={patient.id}
					patient={patient}
					complianceSkeletonProps={complianceSkeletonProps}
				/>
			))
		);
	}
	return (
		<DataTable
			className="p-round-corner"
			tableStyle={{ width: "100%" }}
			value={registeredPatientsRows}
			rowHover={true}
			onRowClick={(e: DataTableRowClickEvent) => {
				const data = e.data as RegisteredPatient;
				navigate(RoutesPath.PatientDetails, {
					route: { patientId: data.id },
					query: { active: TabPatientDetails.PROGRESS_TAB },
				});
				sendAnalyticsEventViewPatientDetail(e.data.analyticsId as string, AnalyticsProgramClickType.LIST_CLICK);
			}}
			dataKey="id"
		>
			<Column
				{...columnProps}
				field="name"
				bodyStyle={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}
				bodyClassName="p-size-s-bold"
				header={format("home.registeredPatientsListing.nameColumn")}
				body={NameDiseaseCell}
			/>
			<Column
				{...columnProps}
				field="programStatusColumn"
				header={format("home.registeredPatientsListing.programStatusColumn")}
				body={ProgramStatusCell}
				sortFunction={e =>
					(e.data as RegisteredPatient[]).sort((a, b) =>
						sortGlobalProgramStatus(a.globalProgramStatus, b.globalProgramStatus, e.order ?? 1),
					)
				}
			/>
			<Column
				{...columnProps}
				field="compliance"
				body={complianceBody}
				header={
					<Tooltip content={format("patientDetails.progress.complianceSection.tooltip")} maxWidth={400} placement="top">
						<span className="p-column-title last-completed-session">
							{format("home.registeredPatientsListing.globalComplianceColumn")}
						</span>
					</Tooltip>
				}
			/>
			<Column
				{...columnProps}
				field="lastCompletedSession"
				header={
					<Tooltip content={format("home.registeredPatientsListing.lastSessionColumn.tooltip")} placement="top">
						<span className="p-column-title last-completed-session">
							{format("home.registeredPatientsListing.lastSessionColumn")}
						</span>
					</Tooltip>
				}
				body={lastSessionBody}
				sortFunction={e =>
					(e.data as RegisteredPatient[]).sort(
						(a, b) =>
							(e.order ?? 1) * ((a.lastCompletedSessionDate?.unix() ?? 0) - (b.lastCompletedSessionDate?.unix() ?? 0)),
					)
				}
			/>
			<Column
				field="extendButton"
				style={{ width: 100 }}
				body={extendProgramBody}
			/>
			<Column
				field="additionalOptions"
				style={{ width: 72 }}
				body={PatientActionCell}
			/>
		</DataTable>
	);
};

const Disease = styled.div`
	color: ${colors.grey} !important;
	margin-top: 4px 0 0 0;
`;

const PatientNameContainer = styled.div`
	max-width: 300px;
	overflow: hidden;
	text-overflow: ellipsis;
	${stack(4)}
`;
const PatientName = styled.p`
	overflow: hidden;
	text-overflow: ellipsis;
`;
